import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@googlemaps/js-api-loader';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import ReactDatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { register as registerAction } from '../../../redux/actions/auth.js';
import { useAlert } from '../../../context/AlertContext';
import { brandSchema } from 'utils/validationSchema';
import { categories } from 'utils/categories';
import { useImageCropper } from 'hooks/useImageCropper';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_ACCEPT,
} from 'utils/uploadValidation';

import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  GlobeAltIcon,
  MapPinIcon,
  CalendarIcon,
  LinkIcon,
  PlusIcon,
  XMarkIcon,
  ArrowLongRightIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

import ImageCropper from 'components/ImageCropper';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import { Card, CardHeader, Field, FieldLabel } from '../primitives';

import FacebookIcon from '../../../assets/icons/socials/facebook_icon.svg';
import InstagramIcon from '../../../assets/icons/socials/instagram_icon.svg';
import TiktokIcon from '../../../assets/icons/socials/tiktok_icon.svg';

import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

import './slider.css';
import 'react-datepicker/dist/react-datepicker.css';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const getComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type));

const extractCountry = (components) => {
  const c = getComponent(components, 'country');
  return { name: c?.long_name || '', code: c?.short_name || '' };
};

const extractCity = (components) =>
  getComponent(components, 'locality')?.long_name ||
  getComponent(components, 'postal_town')?.long_name ||
  getComponent(components, 'administrative_area_level_2')?.long_name ||
  getComponent(components, 'administrative_area_level_1')?.long_name ||
  '';

const isCountryPrediction = (opt) => {
  const types = opt?.value?.types || opt?.types || [];
  return types.includes('country');
};

const isCityLikeTypes = (types = []) =>
  [
    'locality',
    'postal_town',
    'administrative_area_level_3',
    'administrative_area_level_2',
    'administrative_area_level_1',
  ].some((t) => types.includes(t));

export const BrandForm = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [productImagesPreview, setProductImagesPreview] = useState([]);
  const [selectedMacros, setSelectedMacros] = useState([]);
  const [selectedMicroCategories, setSelectedMicroCategories] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [foundingYear, setFoundingYear] = useState(null);
  const [otherLinks, setOtherLinks] = useState([{ label: '', url: '' }]);
  const [, setMapsReady] = useState(false);
  const [countryOption, setCountryOption] = useState(null);
  const [countryCode, setCountryCode] = useState('');
  const [cityOption, setCityOption] = useState(null);

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  const {
    imagePreview,
    croppedImage: croppedAvatar,
    croppedImagePreview,
    isCropping,
    handleImageChange,
    handleCropSave,
    setIsCropping,
    resetImage,
  } = useImageCropper();

  const {
    handleSubmit,
    register: formRegister,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(brandSchema),
    defaultValues: {
      country: '',
      countryCode: '',
      city: '',
      cityPlaceId: '',
      locationCoords: null,
      website: '',
      facebook: '',
      instagram: '',
      tiktok: '',
      description: '',
      longDescription: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const descValue = watch('description') || '';
  const longDescValue = watch('longDescription') || '';
  const brandNameValue = watch('username') || '';

  useEffect(() => {
    let cancelled = false;
    const setReady = () => {
      if (!cancelled) setMapsReady(true);
    };

    if (window.google?.maps?.places) {
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!API_KEY) {
      console.warn('Missing Google Maps API key — skipping Google loader.');
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!window.__gmapsPromise) {
      const loader = new Loader({
        apiKey: API_KEY,
        libraries: ['places'],
        id: 'gmaps-places',
        language: (i18n.language || 'en').split('-')[0],
      });
      window.__gmapsPromise = loader.load();
    }

    window.__gmapsPromise
      .then(() => {
        setReady();
      })
      .catch((err) => {
        console.error('Google Maps JS failed to load', err);
        setReady();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_KEY]);

  const showUploadErrors = (errors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: errors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  const handleAvatarChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors } = validateFiles(files, { kind: 'image', t });
    if (errors.length) {
      showUploadErrors(errors);
      e.target.value = '';
      return;
    }
    if (accepted.length) handleImageChange(e);
  };

  const handleProductImagesChange = (e) => {
    const existingBytes = productImagesPreview.reduce(
      (sum, image) => sum + (image.file?.size || 0),
      0
    );
    const { accepted, errors } = validateFiles(
      Array.from(e.target.files || []),
      { kind: 'image', existingBytes, t }
    );
    if (errors.length) showUploadErrors(errors);

    const files = accepted.slice(0, 10);
    if (files.length === 0) {
      e.target.value = '';
      return;
    }

    const previews = files.map((file) => ({
      id: URL.createObjectURL(file),
      file,
      preview: URL.createObjectURL(file),
    }));

    // Single source of truth: the files uploaded on submit are derived from
    // this list, so removing a tile really removes the file.
    setProductImagesPreview((prevImages) => [
      ...prevImages,
      ...previews.slice(0, 10 - prevImages.length),
    ]);
    e.target.value = '';
  };

  const handleRemoveImage = (id) => {
    setProductImagesPreview((prevImages) =>
      prevImages.filter((image) => image.id !== id)
    );
  };

  const handleDateChange = (date) => {
    const year = moment(date).format('YYYY');
    setFoundingYear(year);
  };

  const addOtherLink = () =>
    setOtherLinks((s) => [...s, { label: '', url: '' }]);
  const removeOtherLink = (idx) =>
    setOtherLinks((s) => s.filter((_, i) => i !== idx));
  const updateOtherLink = (idx, key, value) =>
    setOtherLinks((s) =>
      s.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords((prev) => [...prev, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setKeywords((prev) =>
      prev.filter((keyword) => keyword !== keywordToRemove)
    );
  };

  const onCountryChange = async (place) => {
    if (!place) {
      setCountryOption(null);
      setCountryCode('');
      setCityOption(null);
      setValue('country', '', { shouldValidate: true });
      setValue('countryCode', '', { shouldValidate: false });
      setValue('city', '', { shouldValidate: true });
      setValue('cityPlaceId', '', { shouldValidate: false });
      setValue('locationCoords', null, { shouldValidate: false });
      return;
    }

    setCountryOption(place);
    setCityOption(null);
    setValue('city', '', { shouldValidate: true });
    setValue('cityPlaceId', '', { shouldValidate: false });
    setValue('locationCoords', null, { shouldValidate: false });
    setValue('country', place?.label || '', { shouldValidate: true });
    setCountryCode('');
    setValue('countryCode', '', { shouldValidate: false });

    if (!place?.value?.place_id || !API_KEY) return;

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=${API_KEY}`
      );
      const data = await resp.json();
      const components = data?.results?.[0]?.address_components || [];
      const { name, code } = extractCountry(components);
      const finalCountry = name || place.label || '';
      const finalCode = (code || '').toUpperCase();
      setValue('country', finalCountry, { shouldValidate: true });
      setCountryCode(finalCode);
      setValue('countryCode', finalCode, { shouldValidate: false });
    } catch (e) {
      console.error('Country geocode error:', e);
    }
  };

  const onCityChange = async (place) => {
    if (!place) {
      setCityOption(null);
      setValue('city', '', { shouldValidate: true });
      setValue('cityPlaceId', '', { shouldValidate: false });
      setValue('locationCoords', null, { shouldValidate: false });
      return;
    }

    setCityOption(place);
    const placeId = place?.value?.place_id || '';
    setValue('cityPlaceId', placeId, { shouldValidate: false });

    if (!placeId || !API_KEY) {
      const fallback = (place?.label || '').split(',')[0].trim();
      setValue('city', fallback, { shouldValidate: true });
      setCityOption({ ...place, label: fallback });
      return;
    }

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${API_KEY}`
      );
      const data = await resp.json();
      const result = data?.results?.[0];
      const components = result?.address_components || [];
      const cityName =
        extractCity(components) || (place?.label || '').split(',')[0].trim();
      setValue('city', cityName, { shouldValidate: true });
      setCityOption({ ...place, label: cityName });
      const coords = result?.geometry?.location;
      if (typeof coords?.lat === 'number' && typeof coords?.lng === 'number') {
        setValue(
          'locationCoords',
          { lat: coords.lat, lng: coords.lng },
          { shouldValidate: false }
        );
      } else {
        setValue('locationCoords', null, { shouldValidate: false });
      }
    } catch (e) {
      console.error('City geocode error:', e);
      const fallback = (place?.label || '').split(',')[0].trim();
      setValue('city', fallback, { shouldValidate: true });
      setCityOption({ ...place, label: fallback });
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const formData = new FormData();

    const categoriesArray = selectedMacros.map((macroId) => {
      const cat = categories.find((c) => c.id === Number(macroId));
      const allowedMicroIds = new Set((cat?.micro ?? []).map((m) => m.id));
      const microIds = (selectedMicroCategories || [])
        .map(Number)
        .filter((id) => allowedMicroIds.has(id));
      return {
        macroCategory: Number(macroId),
        microCategory: [...new Set(microIds)],
      };
    });

    if (categoriesArray.length === 0) {
      setIsSubmitting(false);
      showAlert({
        type: 'warning',
        title: t('alert_dialog.error.register.categories_required_title'),
        message: t('alert_dialog.error.register.categories_required_msg'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }

    const combinedError = combinedUploadError(
      [croppedAvatar, ...productImagesPreview.map((image) => image.file)],
      t
    );
    if (combinedError) {
      setIsSubmitting(false);
      showUploadErrors([combinedError]);
      return;
    }

    Object.entries(data).forEach(([key, value]) => {
      if (
        key === 'macroCategory' ||
        key === 'microCategory' ||
        key === 'avatar' ||
        key === 'website' ||
        key === 'facebook' ||
        key === 'instagram' ||
        key === 'tiktok' ||
        key === 'socialsOthers'
      )
        return;

      if (value instanceof FileList) {
        Array.from(value)
          .slice(0, 10)
          .forEach((file) => {
            formData.append('media', file, file.name);
          });
      } else if (typeof value === 'object' && !(value instanceof Date)) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    if (croppedAvatar) {
      if (
        typeof croppedAvatar === 'string' &&
        croppedAvatar.startsWith('data:image')
      ) {
        const response = await fetch(croppedAvatar);
        const blob = await response.blob();
        formData.append('avatar', blob, 'avatar.jpg');
      } else if (
        croppedAvatar instanceof File ||
        croppedAvatar instanceof Blob
      ) {
        formData.append('avatar', croppedAvatar, 'avatar.jpg');
      }
    }

    productImagesPreview
      .map((image) => image.file)
      .filter(Boolean)
      .slice(0, 10)
      .forEach((file) => {
        formData.append('media', file, file.name);
      });

    formData.append('categories', JSON.stringify(categoriesArray));
    if (foundingYear) formData.append('foundingYear', foundingYear);
    formData.append('keywords', JSON.stringify(keywords));

    const socials = {
      website: data.website || null,
      facebook: data.facebook || null,
      instagram: data.instagram || null,
      tiktok: data.tiktok || null,
      others: otherLinks
        .map(({ label, url }) => ({
          label: (label || '').trim(),
          url: (url || '').trim(),
        }))
        .filter((l) => l.label && l.url),
    };
    formData.append('socials', JSON.stringify(socials));

    try {
      await dispatch(registerAction('brands', formData));
      if (onSuccess) onSuccess(data?.email || '');
    } catch (error) {
      const uploadMessage = uploadErrorMessage(error, t);
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.error;
      showAlert({
        type: 'error',
        title: t('alert_dialog.register.error_title'),
        message:
          uploadMessage ||
          serverMessage ||
          t('alert_dialog.register.error_message'),
        primaryAction: { label: t('common.ok') },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectStyles = (isDisabled = false) => ({
    container: (base) => ({ ...base, width: '100%' }),
    control: (base) => ({
      ...base,
      minHeight: 48,
      height: 48,
      backgroundColor: isDisabled ? '#F4F2EC' : '#ffffff',
      borderColor: '#E1DDD2',
      borderRadius: 10,
      boxShadow: 'none',
      paddingLeft: 32,
      outline: 'none',
      '&:hover': { borderColor: '#E1DDD2' },
      cursor: isDisabled ? 'not-allowed' : 'default',
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    placeholder: (base) => ({
      ...base,
      color: '#8A8579',
      fontSize: '14.5px',
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '14.5px' }),
    singleValue: (base) => ({ ...base, color: '#1A1A1F', fontSize: '14.5px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, paddingRight: 12, paddingLeft: 8 }),
    clearIndicator: (base) => ({ ...base, paddingRight: 8, paddingLeft: 8 }),
    menu: (base) => ({
      ...base,
      zIndex: 80,
      borderRadius: 10,
      overflow: 'hidden',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#FAF9F6' : '#FFFFFF',
      color: '#1A1A1F',
      cursor: 'pointer',
      fontSize: '14.5px',
    }),
  });

  const placesAvailable = !!window.google?.maps?.places;

  const sections = [
    { id: 'account', num: '01', label: t('register.section_account', 'Account') },
    { id: 'identity', num: '02', label: t('register.section_identity', 'Identity') },
    { id: 'location', num: '03', label: t('register.section_location', 'Location') },
    { id: 'story', num: '04', label: t('register.section_story', 'Story') },
    { id: 'categories', num: '05', label: t('register.section_categories', 'Categories') },
    { id: 'showcase', num: '06', label: t('register.section_showcase', 'Images') },
    { id: 'web', num: '07', label: t('register.section_web', 'Web & socials') },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr] lg:gap-14">
      {/* TOC */}
      <aside className="hidden lg:block">
        <div className="sticky top-6 flex flex-col gap-0.5">
          <div className="px-2.5 py-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('register.toc_label', 'On this page')}
          </div>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(s.id);
                if (el) {
                  window.scrollTo({
                    top: el.getBoundingClientRect().top + window.scrollY - 80,
                    behavior: 'smooth',
                  });
                }
              }}
              className="flex items-center gap-2.5 rounded-lg border-l-2 border-transparent px-2.5 py-2 text-[13.5px] font-medium text-ink-2 hover:bg-white/60"
            >
              <span className="w-[18px] font-display text-[11px] font-bold text-muted">
                {s.num}
              </span>
              {s.label}
            </a>
          ))}
          <div className="mt-4 rounded-[10px] border border-line bg-white/50 p-3.5 text-xs leading-[1.45] text-muted">
            <strong className="mb-1 block font-semibold text-ink">
              {t('register.toc_save_title', 'Auto-saving as you type')}
            </strong>
            {t('register.toc_save_body', 'You can leave and come back.')}
          </div>
        </div>
      </aside>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
        noValidate
      >
        {/* 01 Account */}
        <Card id="account">
          <CardHeader
            num="01"
            title={t('register.section_account', 'Account')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={t('register.brand_name_label', 'Brand name')}
              icon={<UserIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />}
              error={errors.username?.message}
            >
              <input
                type="text"
                {...formRegister('username')}
                placeholder={t('register.brand_name')}
              />
            </Field>

            <Field
              label={t('register.email_label', 'Email')}
              icon={
                <EnvelopeIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />
              }
              error={errors.email?.message}
            >
              <input
                type="email"
                {...formRegister('email')}
                placeholder={t('register.email')}
              />
            </Field>

            <Field
              label={t('register.password_label', 'Password')}
              icon={
                <LockClosedIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              suffix={{
                icon: showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                ),
                label: showPassword ? 'Hide password' : 'Show password',
                onClick: () => setShowPassword((s) => !s),
              }}
              error={errors.password?.message}
            >
              <input
                type={showPassword ? 'text' : 'password'}
                {...formRegister('password')}
                placeholder={t('register.password')}
              />
            </Field>

            <Field
              label={t('register.confirm_password_label', 'Confirm password')}
              icon={
                <LockClosedIcon
                  className="h-[18px] w-[18px]"
                  strokeWidth={1.7}
                />
              }
              suffix={{
                icon: showConfirmPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                ),
                label: showConfirmPassword ? 'Hide password' : 'Show password',
                onClick: () => setShowConfirmPassword((s) => !s),
              }}
              error={errors.confirmPassword?.message}
            >
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                {...formRegister('confirmPassword')}
                placeholder={t('register.confirm_password')}
              />
            </Field>
          </div>
        </Card>

        {/* 02 Identity */}
        <Card id="identity">
          <CardHeader
            num="02"
            title={t('register.section_identity', 'Identity')}
          />
          <input
            type="file"
            id="logo-upload"
            accept={IMAGE_ACCEPT}
            className="sr-only"
            onChange={handleAvatarChange}
          />
          <div className="flex items-center gap-5">
            {croppedAvatar ? (
              <img
                src={croppedImagePreview || imagePreview}
                alt="Logo Preview"
                className="h-28 w-28 rounded-full border border-field-border object-cover"
              />
            ) : (
              <label
                htmlFor="logo-upload"
                className="grid h-28 w-28 cursor-pointer place-items-center rounded-full border border-field-border bg-paper-2"
              >
                <PhotoIcon className="h-10 w-10 text-muted" strokeWidth={1.5} />
              </label>
            )}
            <div className="flex-1">
              <div className="mb-1 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-muted">
                {t('register.brand_logo', 'Brand logo')}
              </div>
              <div className="mb-2 text-sm text-ink-2">
                {croppedAvatar
                  ? t('register.replace', 'Replace') +
                    ' / ' +
                    t('register.remove', 'Remove')
                  : t('register.upload_logo', 'Upload Logo')}
              </div>
              <div className="flex gap-2">
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer rounded-[10px] border border-line-strong px-3.5 py-2 text-[13px] font-semibold text-ink hover:bg-paper-2"
                >
                  {t('register.replace', 'Replace')}
                </label>
                {croppedAvatar && (
                  <button
                    type="button"
                    onClick={() => {
                      resetImage();
                      const el = document.getElementById('logo-upload');
                      if (el) el.value = '';
                    }}
                    className="rounded-[10px] px-3.5 py-2 text-[13px] font-semibold text-red-500 hover:bg-red-50"
                  >
                    {t('register.remove', 'Remove')}
                  </button>
                )}
              </div>
            </div>
          </div>
          {errors.avatar && (
            <p className="text-xs text-red-500">{errors.avatar.message}</p>
          )}
          {isCropping && imagePreview && (
            <ImageCropper
              imageSrc={imagePreview}
              aspect={1}
              shape="round"
              onCropSave={handleCropSave}
              onClose={() => setIsCropping(false)}
            />
          )}
        </Card>

        {/* 03 Location */}
        <Card id="location">
          <CardHeader
            num="03"
            title={t('register.section_location', 'Location')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-[7px]">
              <FieldLabel>{t('register.country_label', 'Country')}</FieldLabel>
              <div className="relative">
                <GlobeAltIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-muted"
                  strokeWidth={1.7}
                />
                {placesAvailable ? (
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{ types: ['(regions)'] }}
                    selectProps={{
                      noOptionsMessage: () => t('homepage.no_options'),
                      value: countryOption,
                      onChange: onCountryChange,
                      placeholder: t('register.country'),
                      isClearable: true,
                      styles: selectStyles(false),
                      filterOption: (option) =>
                        isCountryPrediction(option?.data || option),
                    }}
                  />
                ) : (
                  <input
                    disabled
                    placeholder={t('register.country')}
                    className="w-full appearance-none rounded-[10px] border border-field-border bg-paper-2 px-3.5 py-3 pl-11 text-[14.5px] text-ink outline-none"
                  />
                )}
              </div>
              <input type="hidden" {...formRegister('country')} />
              {errors.country && (
                <p className="text-xs text-red-500">{errors.country.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-[7px]">
              <FieldLabel>{t('register.city_label', 'City')}</FieldLabel>
              <div className="relative">
                <MapPinIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-muted"
                  strokeWidth={1.7}
                />
                {placesAvailable ? (
                  <GooglePlacesAutocomplete
                    autocompletionRequest={{
                      types: ['(cities)'],
                      ...(countryCode
                        ? { componentRestrictions: { country: countryCode } }
                        : {}),
                    }}
                    selectProps={{
                      value: cityOption,
                      onChange: onCityChange,
                      noOptionsMessage: () => t('homepage.no_options'),
                      placeholder: countryOption
                        ? t('register.city')
                        : t('register.select_country_first'),
                      isDisabled: !countryOption,
                      isClearable: true,
                      styles: selectStyles(!countryOption),
                      filterOption: (option) => {
                        const o = option?.data || option;
                        const types = o?.value?.types || o?.types || [];
                        return isCityLikeTypes(types);
                      },
                    }}
                  />
                ) : (
                  <input
                    disabled
                    placeholder={t('register.select_country_first')}
                    className="w-full appearance-none rounded-[10px] border border-field-border bg-paper-2 px-3.5 py-3 pl-11 text-[14.5px] text-ink outline-none"
                  />
                )}
              </div>
              <input type="hidden" {...formRegister('city')} />
              <input type="hidden" {...formRegister('countryCode')} />
              <input type="hidden" {...formRegister('cityPlaceId')} />
              <input type="hidden" {...formRegister('locationCoords')} />
              {errors.city && (
                <p className="text-xs text-red-500">{errors.city.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-[7px]">
              <FieldLabel optional={t('register.optional', 'optional')}>
                {t('register.founding_year_label', 'Founding year')}
              </FieldLabel>
              <div className="relative">
                <CalendarIcon
                  className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-[18px] w-[18px] -translate-y-1/2 text-muted"
                  strokeWidth={1.7}
                />
                <ReactDatePicker
                  selected={
                    foundingYear ? moment(foundingYear, 'YYYY').toDate() : null
                  }
                  onChange={handleDateChange}
                  showYearPicker
                  dateFormat="yyyy"
                  placeholderText={t('register.founding_year')}
                  className="w-full appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-3 pl-11 text-[14.5px] text-ink outline-none transition focus:border-ink"
                  maxDate={new Date()}
                  popperPlacement="top-end"
                />
              </div>
            </div>

            <Field
              label={t('register.website_label', 'Website')}
              optional={t('register.optional', 'optional')}
              icon={<LinkIcon className="h-[18px] w-[18px]" strokeWidth={1.7} />}
              error={errors.website?.message}
            >
              <input
                type="text"
                {...formRegister('website')}
                placeholder={t('register.website')}
              />
            </Field>
          </div>
        </Card>

        {/* 04 Story */}
        <Card id="story">
          <CardHeader
            num="04"
            title={t('register.section_story', 'Story')}
          />
          <div className="flex flex-col gap-[7px]">
            <FieldLabel optional={t('register.tagline_hint', '200 max')}>
              {t('register.tagline_label', 'Tagline')}
            </FieldLabel>
            <div className="relative">
              <textarea
                {...formRegister('description', { maxLength: 200 })}
                maxLength={200}
                rows={2}
                className="w-full resize-none appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-3 text-[14.5px] leading-[1.5] text-ink outline-none transition focus:border-ink"
              />
              <div
                className="absolute bottom-2.5 right-3.5 text-[11px] tabular-nums text-muted"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, white 30%)',
                  paddingLeft: 22,
                }}
              >
                {descValue.length} / 200
              </div>
            </div>
            {errors.description && (
              <p className="text-xs text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-[7px]">
            <FieldLabel>{t('register.about_label', 'About your brand')}</FieldLabel>
            <div className="relative">
              <textarea
                {...formRegister('longDescription', { maxLength: 10000 })}
                maxLength={10000}
                rows={6}
                className="w-full resize-none appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-3 text-[14.5px] leading-[1.5] text-ink outline-none transition focus:border-ink"
              />
              <div
                className="absolute bottom-2.5 right-3.5 text-[11px] tabular-nums text-muted"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, white 30%)',
                  paddingLeft: 22,
                }}
              >
                {longDescValue.length} / 10000
              </div>
            </div>
            {errors.longDescription && (
              <p className="text-xs text-red-500">
                {errors.longDescription.message}
              </p>
            )}
          </div>
        </Card>

        {/* 05 Categories */}
        <Card id="categories">
          <CardHeader
            num="05"
            title={t('register.section_categories', 'Categories & keywords')}
          />

          <div className="flex flex-col gap-[7px]">
            <FieldLabel optional={t('register.keywords_optional', 'help search find you')}>
              {t('register.keywords', 'Keywords')}
            </FieldLabel>
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white/70 px-3 py-[5px] text-[12.5px] font-medium text-ink"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="grid h-4 w-4 place-items-center rounded-full bg-black/[0.08] text-[11px] leading-none text-ink-2 hover:bg-black/[0.15]"
                    aria-label="Remove keyword"
                  >
                    ×
                  </button>
                </span>
              ))}
              <div className="inline-flex items-center gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                  placeholder={t('register.keyword_placeholder', 'Enter a keyword')}
                  className="min-w-[240px] rounded-full border border-dashed border-line-strong bg-transparent px-4 py-[7px] text-[13px] text-ink outline-none placeholder:text-muted focus:border-ink"
                />
                {newKeyword.trim() && (
                  <button
                    type="button"
                    onClick={handleAddKeyword}
                    className="grid h-6 w-6 place-items-center rounded-full bg-ink text-white"
                    aria-label="Add keyword"
                  >
                    <PlusIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <CategorySelectorGrid
            selectedMacros={selectedMacros}
            setSelectedMacros={setSelectedMacros}
            selectedMicros={selectedMicroCategories}
            setSelectedMicros={setSelectedMicroCategories}
          />
          {errors.macroCategory && (
            <p className="text-xs text-red-500">{errors.macroCategory.message}</p>
          )}
        </Card>

        {/* 06 Images */}
        <Card id="showcase">
          <CardHeader
            num="06"
            title={t('register.section_showcase', 'Images')}
          />
          <input
            type="file"
            multiple
            id="product-images-upload"
            accept={IMAGE_ACCEPT}
            className="sr-only"
            onChange={handleProductImagesChange}
          />
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-5">
            {productImagesPreview.map((image) => (
              <div
                key={image.id}
                className="relative aspect-square overflow-hidden rounded-[10px] bg-paper-2"
              >
                <img
                  src={image.preview}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(image.id)}
                  className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-white/95 text-[11px] text-red-500 hover:bg-white"
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            {productImagesPreview.length < 10 && (
              <label
                htmlFor="product-images-upload"
                className="grid aspect-square cursor-pointer place-items-center rounded-[10px] border-2 border-dashed border-field-border text-center font-display text-[11.5px] font-bold uppercase tracking-[0.1em] text-muted hover:border-muted hover:text-ink"
              >
                <div>
                  <PlusIcon className="mx-auto h-[18px] w-[18px]" strokeWidth={2} />
                  <div className="mt-1">{t('register.add_photo', 'Add')}</div>
                </div>
              </label>
            )}
          </div>
          {errors.productImages && (
            <p className="text-xs text-red-500">{errors.productImages.message}</p>
          )}
        </Card>

        {/* 07 Web & socials */}
        <Card id="web">
          <CardHeader
            num="07"
            title={t('register.section_web', 'Web & socials')}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={t('register.instagram', 'Instagram')}
              optional={t('register.optional', 'optional')}
              icon={
                <img src={InstagramIcon} alt="" className="h-[18px] w-[18px]" />
              }
              error={errors.instagram?.message}
            >
              <input
                type="text"
                {...formRegister('instagram')}
                placeholder="https://instagram.com/yourhandle"
              />
            </Field>
            <Field
              label={t('register.facebook', 'Facebook')}
              optional={t('register.optional', 'optional')}
              icon={
                <img src={FacebookIcon} alt="" className="h-[18px] w-[18px]" />
              }
              error={errors.facebook?.message}
            >
              <input
                type="text"
                {...formRegister('facebook')}
                placeholder="https://facebook.com/yourpage"
              />
            </Field>
            <Field
              label={t('register.tiktok', 'TikTok')}
              optional={t('register.optional', 'optional')}
              icon={<img src={TiktokIcon} alt="" className="h-[18px] w-[18px]" />}
              error={errors.tiktok?.message}
            >
              <input
                type="text"
                {...formRegister('tiktok')}
                placeholder="https://tiktok.com/@yourhandle"
              />
            </Field>

            <div className="md:col-span-2">
              <FieldLabel>
                {t('register.other_links', 'Other links')}
              </FieldLabel>
              <div className="mt-[7px] flex flex-col gap-2">
                {otherLinks.map((row, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      value={row.label}
                      onChange={(e) =>
                        updateOtherLink(idx, 'label', e.target.value)
                      }
                      placeholder={t('register.link_label_placeholder')}
                      className="w-1/3 appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
                    />
                    <input
                      value={row.url}
                      onChange={(e) =>
                        updateOtherLink(idx, 'url', e.target.value)
                      }
                      placeholder={t('register.link_url_placeholder')}
                      className="flex-1 appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-ink"
                    />
                    <button
                      type="button"
                      onClick={() => removeOtherLink(idx)}
                      className="grid h-10 w-10 place-items-center rounded-[10px] text-muted hover:bg-paper-2 hover:text-red-500"
                      aria-label="Remove link"
                    >
                      <XMarkIcon className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addOtherLink}
                  className="inline-flex items-center gap-2 self-start rounded-full border border-dashed border-line-strong bg-transparent px-3 py-[5px] text-[12.5px] font-medium text-muted hover:border-ink hover:text-ink"
                >
                  <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} />
                  {t('register.add_link', 'Add link')}
                </button>
              </div>
              {errors.socialsOthers && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.socialsOthers.message}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Submit bar */}
        <div className="mt-2 flex flex-col gap-3.5 rounded-2xl bg-ink p-5 text-white lg:flex-row lg:items-center lg:justify-between lg:p-8">
          <div>
            <div className="text-[17px] font-extrabold leading-[1.25] tracking-[-0.01em] lg:text-[20px]">
              {t(
                'register.submit_brand_title',
                'Ready to introduce {{name}} to the world?'
              )
                .split(/<em>(.*?)<\/em>/)
                .map((part, i) => {
                  if (i % 2 === 1) {
                    const value = part.includes('{{name}}')
                      ? brandNameValue ||
                        t('register.submit_brand_fallback', 'your brand')
                      : part;
                    return (
                      <em
                        key={i}
                        className="italic font-serif font-medium text-brand"
                      >
                        {value}
                      </em>
                    );
                  }
                  return <span key={i}>{part}</span>;
                })}
            </div>
            <span className="mt-1.5 block text-[11.5px] text-white/55">
              {t(
                'register.submit_terms',
                'By creating an account you agree to our Terms and Privacy Policy.'
              )
                .split(/<a>(.*?)<\/a>/)
                .map((part, i) =>
                  i % 2 === 1 ? (
                    <a
                      key={i}
                      href={i === 1 ? '/terms' : '/privacy'}
                      className="text-white/85 underline"
                    >
                      {part}
                    </a>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
            </span>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-brand bg-brand px-5 py-3.5 text-[15px] font-semibold text-ink transition hover:bg-brand-hover disabled:opacity-70"
          >
            {isSubmitting ? (
              <ClipLoader color="#1A1A1F" size={18} />
            ) : (
              <>
                {t('register.submit_brand_cta', 'Create brand account')}
                <ArrowLongRightIcon className="h-4 w-4" strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BrandForm;
